import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material';
import {
  FileUpload as FileUploadIcon,
  Code as CodeIcon,
  Help as HelpIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { coursesAPI } from '../../services/api';
import { Button, Card, TextField } from './index';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const CourseJsonImport = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation('courses');

  // Check if user has permission to import courses
  const hasImportPermission =
    user?.userMeta?.roles?.includes('admin') ||
    user?.userMeta?.roles?.includes('teacher');

  const handleOpen = () => {
    if (!hasImportPermission) {
      toast.error(t('courses:courseImport.noPermission'));
      return;
    }
    setOpen(true);
    setError('');
    setJsonText('');
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setShowStructure(false);
  };

  const handleImport = async () => {
    try {
      if (!hasImportPermission) {
        setError(t('courses:courseImport.noPermission'));
        return;
      }

      if (!jsonText.trim()) {
        setError(t('courses:courseImport.validation.emptyJson'));
        return;
      }

      let parsedData;
      try {
        parsedData = JSON.parse(jsonText);
      } catch (err) {
        setError(t('courses:courseImport.validation.invalidJson') + err.message);
        return;
      }

      // Validate course required fields
      if (!parsedData.title || !parsedData.description || !parsedData.difficulty) {
        setError(t('courses:courseImport.validation.requiredFields'));
        return;
      }

      // Validate difficulty enum
      if (!['beginner', 'intermediate', 'advanced'].includes(parsedData.difficulty)) {
        setError(t('courses:courseImport.validation.invalidDifficulty'));
        return;
      }

      // Validate modules if present
      if (parsedData.modules && Array.isArray(parsedData.modules)) {
        for (let i = 0; i < parsedData.modules.length; i++) {
          const module = parsedData.modules[i];
          if (!module.title || !module.content) {
            setError(t('courses:courseImport.validation.moduleRequiredFields', { index: i + 1 }));
            return;
          }
        }
      }

      // Add user data to the request
      const dataWithUser = {
        courseData: parsedData,
        user,
      };

      setLoading(true);
      const response = await coursesAPI.importFromJson(dataWithUser);

      if (response && response.data) {
        toast.success(t('courses:courseImport.success'));
        handleClose();
        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (err) {
      console.error('Ошибка при импорте курса:', err);
      if (err.message?.includes('JSON')) {
        setError(t('courses:courseImport.validation.invalidJson') + err.message);
      } else if (err.response?.data?.message) {
        setError(t('courses:courseImport.error.import') + err.response.data.message);
      } else {
        setError(
          t('courses:courseImport.error.import') +
            (err.message || t('courses:courseImport.error.unknown'))
        );
      }
      setLoading(false);
    }
  };

  // Don't render the component if user doesn't have permission
  if (!hasImportPermission) {
    return null;
  }

  const toggleStructure = () => {
    setShowStructure(!showStructure);
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        setJsonText(content);
        JSON.parse(content);
        setError('');
      } catch (err) {
        setError(t('courses:courseImport.validation.invalidJson') + err.message);
      }
    };
    reader.readAsText(file);
  };

  const jsonStructure = {
    title: t('courses:courseImport.jsonStructure.title'),
    description: t('courses:courseImport.jsonStructure.description'),
    difficulty: t('courses:courseImport.jsonStructure.difficulty'),
    modules: [
      {
        title: t('courses:courseImport.jsonStructure.modules.title'),
        content: t('courses:courseImport.jsonStructure.modules.content'),
      },
    ],
  };

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CodeIcon />}
        onClick={handleOpen}
        sx={{ ml: 1 }}
        size="small"
        data-testid="import-button"
      >
        {t('courses:courseImport.button')}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 1, md: 2 },
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={2}>
            <CloudUploadIcon
              color="primary"
              sx={{ fontSize: 28, display: { xs: 'none', md: 'block' } }}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('courses:courseImport.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('courses:courseImport.subtitle')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                component="label"
                startIcon={<FileUploadIcon />}
                size="medium"
              >
                {t('courses:courseImport.uploadFile')}
                <input
                  type="file"
                  accept=".json"
                  hidden
                  onChange={handleUpload}
                />
              </Button>
              <Tooltip title={t('courses:courseImport.showStructure')}>
                <IconButton
                  onClick={toggleStructure}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            {error && (
              <Card
                sx={{
                  backgroundColor: (theme) => theme.palette.error.light,
                  color: 'white',
                  border: 'none',
                }}
              >
                <Typography variant="body2">{error}</Typography>
              </Card>
            )}

            <TextField
              multiline
              fullWidth
              rows={12}
              value={jsonText}
              onChange={(e) => {
                if (error) {
                  setError('');
                }
                setJsonText(e.target.value);
              }}
              error={!!error}
              placeholder={t('courses:courseImport.dialog.placeholder')}
            />

            {showStructure && (
              <Card>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t('courses:courseImport.dialog.structure')}
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={10}
                  value={JSON.stringify(jsonStructure, null, 2)}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                    },
                  }}
                />
              </Card>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} variant="outlined">
            {t('courses:courseImport.dialog.cancel')}
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!jsonText.trim() || !!error || loading}
            startIcon={<CloudUploadIcon />}
          >
            {loading
              ? t('courses:courseImport.dialog.importing')
              : t('courses:courseImport.dialog.import')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CourseJsonImport;
